import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  styled,
  useTheme,
} from "@mui/material";
import {
  Add,
  Delete,
  Download,
  Email,
  Refresh,
} from "@mui/icons-material";
import AppPaginationBar from "components/AppPaginationBar";
import FlexBetween from "components/flexbox/FlexBetween";
import ConfirmationDialog from "components/ConfirmationDialog";
import { H6, Small } from "components/Typography";
import { usePageTitle } from "contexts/PageTitleContext";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import buttonConfig from "../../config/ConfigAccessButton";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "20px 16px",
  borderBottom: `1px solid ${theme.palette.divider}`,
  whiteSpace: "normal",
  wordBreak: "break-word",
  textAlign: "center",
  verticalAlign: "middle",
  [theme.breakpoints.down("md")]: {
    padding: "18px 12px",
    fontSize: "0.8125rem",
  },
  "&.header": {
    fontWeight: 600,
    fontSize: "0.875rem",
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.grey[900]
        : theme.palette.grey[200],
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const tableCellCenterSx = {
  textAlign: "center",
  "& .MuiTypography-root": {
    textAlign: "center",
    width: "100%",
  },
};

const tableCellFlexCenterSx = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  textAlign: "center",
};

const reportTableContainerSx = {
  width: "100%",
  maxWidth: "100%",
  borderRadius: 2,
  overflowX: { xs: "auto", md: "hidden" },
  overflowY: "auto",
  minHeight: 700,
  maxHeight: 800,
  WebkitOverflowScrolling: "touch",
};

const reportTableSx = {
  tableLayout: "fixed",
  width: "100%",
  minWidth: { xs: 960, sm: 1040, md: "100%" },
};

const reportColumnSx = {
  srNo: { width: "5%", minWidth: 48, px: 1, whiteSpace: "nowrap" },
  reportName: { width: "12%", minWidth: 96, px: 1 },
  type: { width: "11%", px: 1 },
  dateRange: { width: "12%", px: 1 },
  company: { width: "22%", px: 1 },
  generatedBy: { width: "10%", px: 1 },
  generated: { width: "13%", px: 1 },
  actions: { width: "10%", minWidth: { xs: 112, md: 100 }, px: 1, whiteSpace: "nowrap" },
};

const actionsCellSx = {
  verticalAlign: "middle",
  textAlign: "center",
  borderLeft: "none",
  boxShadow: "none",
  backgroundImage: "none",
};

const actionsContentSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.75,
  verticalAlign: "middle",
};

const typeCellSx = {
  verticalAlign: "middle",
  textAlign: "center",
};

const typeContentSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  verticalAlign: "middle",
};

const REPORT_TYPES = [
  { value: "revenue_summary", label: "Revenue Summary" },
  { value: "daily_summary", label: "Orders Report" },
  { value: "delivery_records", label: "Delivery Records" },
  { value: "dashboard_report", label: "Dashboard Report" },
  { value: "farmer_activity", label: "Farmer Activity" },
];

const ANALYTICS_TYPES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const REVENUE_PERIOD_TYPES = [
  ...ANALYTICS_TYPES,
  { value: "custom", label: "Custom" },
];

const ORDER_TYPES = [
  { value: "app", label: "App" },
  { value: "bulk", label: "Bulk" },
];

const normalizeOrderTypes = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const DOWNLOAD_FORMATS = [
  { value: "excel", label: "Excel" },
  { value: "pdf", label: "PDF" },
];

const isDeliveryRecordsReport = (reportType) =>
  reportType === "delivery_records" || reportType === "dashboard_report";

const isRevenueSummaryReport = (reportType) => reportType === "revenue_summary";

const isFarmerActivityReport = (reportType) => reportType === "farmer_activity";

const usesPeriodBasedAnalytics = (reportType) =>
  REPORT_TYPES.some((type) => type.value === reportType) &&
  !isFarmerActivityReport(reportType);

const getAnalyticsTypes = (reportType) => {
  if (isFarmerActivityReport(reportType)) return REVENUE_PERIOD_TYPES;
  return usesPeriodBasedAnalytics(reportType) ? REVENUE_PERIOD_TYPES : ANALYTICS_TYPES;
};

const getAnalyticsTypeLabel = (value, reportType) =>
  getAnalyticsTypes(reportType).find((t) => t.value === value)?.label || value || "—";

const shouldShowReportDateFields = (reportType, analyticsType) =>
  (isFarmerActivityReport(reportType) && analyticsType === "custom") ||
  (!isFarmerActivityReport(reportType) &&
    (!usesPeriodBasedAnalytics(reportType) || analyticsType === "custom"));

function parseJsonField(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return {};
}

function normalizeReportRow(row) {
  if (!row || typeof row !== "object") return null;

  const filters = parseJsonField(row.filters_json ?? row.filters);
  const dataJson = parseJsonField(row.data_json ?? row.data);
  const overall = dataJson?.overall_company_data || {};

  return {
    ...row,
    id: row.id ?? row.report_id ?? row.pk,
    report_name: row.report_name ?? row.name ?? "—",
    report_type: row.report_type ?? row.type ?? "—",
    generated_by_name: row.generated_by_name ?? row.user_name ?? "—",
    generated_at: row.generated_at ?? row.created_at ?? row.created,
    filters_json: filters,
    data_json: dataJson,
    analytics_type:
      filters.period ||
      filters.analytics_type ||
      dataJson.period ||
      overall.sales_analytics_type ||
      "—",
    total_revenue: overall.total_revenue ?? 0,
    total_orders: overall.total_orders ?? 0,
    total_companies: overall.total_companies ?? dataJson?.companies?.length ?? 0,
    is_deleted: row.is_deleted === true,
  };
}

function extractReportsList(response) {
  if (!response) return [];

  let rawList = [];

  if (Array.isArray(response)) {
    rawList = response;
  } else if (response.success !== undefined) {
    const inner = response.data;
    if (Array.isArray(inner)) rawList = inner;
    else if (inner && typeof inner === "object") {
      rawList =
        inner.reports ||
        inner.results ||
        inner.daily_reports ||
        (Object.values(inner).find((v) => Array.isArray(v)) || []);
    }
  } else {
    rawList =
      response.reports ||
      response.results ||
      response.daily_reports ||
      (Array.isArray(response.data) ? response.data : []) ||
      (response.data?.reports || []);
  }

  return rawList
    .map(normalizeReportRow)
    .filter((r) => r && !r.is_deleted);
}

function extractReportsPagination(response, pageSize, currentPage) {
  const inner = response?.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const totalItems = inner.count ?? inner.total_items ?? 0;
    const totalPages = inner.total_pages ?? 1;
    return {
      current_page: inner.current_page ?? currentPage ?? 1,
      page_size: inner.page_size ?? pageSize ?? 10,
      total_items: totalItems,
      total_pages: totalPages,
    };
  }

  const list = extractReportsList(response);
  return {
    current_page: currentPage ?? 1,
    page_size: pageSize ?? 10,
    total_items: list.length,
    total_pages: Math.max(1, Math.ceil(list.length / (pageSize || 10))),
  };
}

function extractCreatedReport(response) {
  if (!response) return null;
  const candidates = [response.report, response.data?.report, response.data, response];
  for (const item of candidates) {
    if (item && typeof item === "object" && !Array.isArray(item)) {
      const normalized = normalizeReportRow(item);
      if (normalized?.id != null || normalized?.report_name) return normalized;
    }
  }
  return null;
}

function buildReportFromPayload(response, payload) {
  const reportFromResponse = extractCreatedReport(response);
  if (reportFromResponse) return reportFromResponse;

  const filters = payload?.filters_json || {};
  const now = new Date().toISOString();

  return normalizeReportRow({
    id: response?.data?.id ?? response?.id ?? null,
    report_name: payload?.report_name ?? "New Report",
    report_type: payload?.report_type ?? "daily_summary",
    generated_by_name: apiService.getCurrentUserName?.() || apiService.getCurrentUserId?.() || "—",
    generated_at: now,
    filters_json: filters,
    data_json: {},
  });
}

function mergeReportIntoList(prevList, newReport) {
  if (!newReport) return prevList;
  const exists = prevList.some((item) => String(item.id) === String(newReport.id));
  if (exists) {
    return prevList.map((item) =>
      String(item.id) === String(newReport.id) ? newReport : item
    );
  }
  return [newReport, ...prevList];
}

function extractReportDetail(response) {
  if (!response) return null;
  const raw = response.report ?? response.data?.report ?? response.data ?? response;
  return normalizeReportRow(raw) || raw;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCurrency(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
}

const REPORT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

function tryAddReportEmail(input, currentList = []) {
  const trimmed = String(input || "").trim();
  if (!trimmed) {
    return { ok: false, error: "Enter an email address" };
  }
  if (!REPORT_EMAIL_REGEX.test(trimmed)) {
    return { ok: false, error: "Enter a valid email address" };
  }
  if (currentList.includes(trimmed)) {
    return { ok: false, error: "Email already added" };
  }
  return { ok: true, email: trimmed };
}

function ReportEmailChipInput({
  emails = [],
  inputValue = "",
  onInputChange,
  onAdd,
  onRemove,
  error = "",
  placeholder = "Add email",
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAdd();
            }
          }}
          error={!!error}
          helperText={error || " "}
          FormHelperTextProps={{ sx: { minHeight: 20, m: 0 } }}
        />
        <Button
          variant="contained"
          onClick={onAdd}
          startIcon={<Add />}
          sx={{ minWidth: 92, textTransform: "none", flexShrink: 0 }}
        >
          Add
        </Button>
      </Box>
      {emails.length > 0 ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {emails.map((email) => (
            <Chip
              key={email}
              label={email}
              size="small"
              variant="outlined"
              color="primary"
              onDelete={() => onRemove(email)}
            />
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          No emails added yet
        </Typography>
      )}
    </Box>
  );
}

function getTodayDateString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDateRangeLabel(filters) {
  if (!filters || typeof filters !== "object") return "—";
  const start = filters.start_date || filters.from_date;
  const end = filters.end_date || filters.to_date;
  if (start && end) return `${start} → ${end}`;
  if (start) return String(start);
  return "—";
}

const companyChipSx = {
  height: 24,
  fontSize: "0.8125rem",
  maxWidth: 150,
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const VISIBLE_COMPANY_COUNT = 2;

function getCompanyNames(row, companies = []) {
  if (Array.isArray(row.company_names) && row.company_names.length > 0) {
    return row.company_names.filter(Boolean);
  }

  const filters = row.filters_json || {};
  const dataCompanies = row.data_json?.companies || [];

  if (dataCompanies.length > 0) {
    return dataCompanies.map((c) => c.company_name).filter(Boolean);
  }

  const ids = filters.company_ids || (filters.company ? [filters.company] : []);
  const idList = Array.isArray(ids) ? ids : [ids].filter(Boolean);

  if (!idList.length) return [];

  return idList
    .map((id) => {
      const fromState = companies.find((c) => String(c.id) === String(id));
      return fromState?.name || `Company #${id}`;
    })
    .filter(Boolean);
}

function ReportCompanyCell({ row, companies }) {
  const theme = useTheme();
  const names = getCompanyNames(row, companies);
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  if (names.length === 0 || (companies.length > 0 && names.length === companies.length)) {
    return (
      <Box sx={tableCellFlexCenterSx}>
        <Chip
          size="small"
          label="All Companies"
          variant="outlined"
          sx={companyChipSx}
        />
      </Box>
    );
  }
  const visibleNames = names.slice(0, VISIBLE_COMPANY_COUNT);
  const hiddenCount = names.length - visibleNames.length;

  return (
    <Box
      sx={{
        ...tableCellFlexCenterSx,
        flexWrap: "wrap",
        gap: 0.5,
      }}
    >
      {visibleNames.map((name, index) => (
        <Chip
          key={`${name}-${index}`}
          size="small"
          label={name}
          variant="outlined"
          color="primary"
          sx={companyChipSx}
        />
      ))}
      {hiddenCount > 0 && (
        <>
          <Chip
            ref={anchorRef}
            size="small"
            label={`+${hiddenCount} more`}
            variant="filled"
            color="primary"
            clickable
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            sx={{
              ...companyChipSx,
              fontWeight: 600,
              maxWidth: "none",
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
              },
              "&.MuiChip-clickable:hover": {
                bgcolor: theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
              },
              "&.Mui-focusVisible": {
                bgcolor: theme.palette.primary.dark,
                color: theme.palette.primary.contrastText,
              },
            }}
          />
          <Popover
            open={open}
            onClose={() => setOpen(false)}
            anchorEl={anchorRef.current}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              sx: {
                maxWidth: 360,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[4],
              },
            }}
          >
            <Box sx={{ p: 1.5 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                display="block"
                mb={1}
              >
                All Companies ({names.length})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 200, overflow: "auto" }}>
                {names.map((name, index) => (
                  <Chip
                    key={`all-${name}-${index}`}
                    size="small"
                    label={name}
                    variant="outlined"
                    color="primary"
                    sx={companyChipSx}
                  />
                ))}
              </Box>
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
}

const defaultCreateForm = () => ({
  report_type: "daily_summary",
  analytics_type: "weekly",
  start_date: "",
  end_date: "",
  company_ids: [],
  order_type: [],
  send_email: false,
  download_format: "excel",
  download_formats: ["excel"],
  emails: [],
  emailInput: "",
  emailError: "",
});

const Report = () => {
  const theme = useTheme();
  const { setPageTitle } = usePageTitle();
  const { showSuccess, showError } = useToast();
  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();

  const reportConfig = buttonConfig["Report"] || {};
  const can = (action) => !!reportConfig[action];

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companiesMenuOpen, setCompaniesMenuOpen] = useState(false);
  const [orderTypeMenuOpen, setOrderTypeMenuOpen] = useState(false);
  const [orderTypeError, setOrderTypeError] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(() => defaultCreateForm());
  const [creating, setCreating] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reportDetail, setReportDetail] = useState(null);

  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [downloadMenuReport, setDownloadMenuReport] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [emailLoadingId, setEmailLoadingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, report: null });

  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [sendEmailReportId, setSendEmailReportId] = useState(null);
  const [sendEmailInput, setSendEmailInput] = useState("");
  const [sendEmailList, setSendEmailList] = useState([]);
  const [sendEmailError, setSendEmailError] = useState("");

  const loadCompanies = async () => {
    if (isCompanyUser) {
      const details = apiService.getCompanyDetails();
      const id = apiService.getCompanyId();
      setCompanies([{ id, name: details?.name || "My Company" }]);
      return;
    }
    setLoadingCompanies(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_company);
      const list = response?.data?.Company || response?.data || [];
      setCompanies(Array.isArray(list) ? list : []);
    } catch {
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadReports = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      if (!silent) setLoading(true);
      try {
        const response = await apiService.get(
          `${API_ENDPOINTS.GET_reports_list}?page=${currentPage}&page_size=${pageSize}`
        );
        const list = extractReportsList(response);
        setReports(list);
        setPagination(extractReportsPagination(response, pageSize, currentPage));
        return list;
      } catch (err) {
        const message =
          typeof err === "string"
            ? err
            : err?.message || err?.detail || "Failed to load reports";
        if (!silent) showError(message);
        setReports([]);
        setPagination({
          current_page: currentPage,
          page_size: pageSize,
          total_items: 0,
          total_pages: 1,
        });
        return [];
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [currentPage, pageSize, showError]
  );

  useEffect(() => {
    setPageTitle("Report");
    loadCompanies();
  }, [setPageTitle]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handlePageChange = (_, value) => setCurrentPage(value);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const openCreateDialog = () => {
    if (!can("create")) {
      showError("You don't have permission to create reports");
      return;
    }
    const form = defaultCreateForm();
    if (isCompanyUser && companyId) {
      form.company_ids = [String(companyId)];
    }
    setCreateForm(form);
    setOrderTypeError("");
    setCreateOpen(true);
  };

 

  const handleCreateReport = async () => {
    if (!can("create")) {
      showError("You don't have permission to create reports");
      return;
    }
    const showDateFields = shouldShowReportDateFields(
      createForm.report_type,
      createForm.analytics_type
    );
    if (showDateFields) {
      if (!createForm.start_date || !createForm.end_date) {
        showError("Start date and end date are required");
        return;
      }

      const today = getTodayDateString();
      if (createForm.start_date > today) {
        showError("Start date cannot be after today");
        return;
      }
      if (createForm.end_date > today) {
        showError("End date cannot be after today");
        return;
      }
      if (createForm.end_date < createForm.start_date) {
        showError("End date cannot be before start date");
        return;
      }
    }
    let emailsToSend = [...createForm.emails];
    if (createForm.send_email && createForm.emailInput.trim()) {
      const pendingEmail = tryAddReportEmail(createForm.emailInput, emailsToSend);
      if (!pendingEmail.ok) {
        showError(pendingEmail.error);
        return;
      }
      emailsToSend.push(pendingEmail.email);
    }
    if (createForm.send_email && emailsToSend.length === 0) {
      showError("Please add at least one email address");
      return;
    }
    if (
      createForm.send_email &&
      isRevenueSummaryReport(createForm.report_type) &&
      !createForm.download_formats.length
    ) {
      showError("Please select at least one download format");
      return;
    }
    if (
      !isRevenueSummaryReport(createForm.report_type) &&
      !isFarmerActivityReport(createForm.report_type) &&
      !normalizeOrderTypes(createForm.order_type).length
    ) {
      setOrderTypeError("Order type is required");
      return;
    }
    setOrderTypeError("");

    const userId = apiService.getCurrentUserId();
    setCreating(true);
    try {
      const showDateFields = shouldShowReportDateFields(
        createForm.report_type,
        createForm.analytics_type
      );
      const filters_json = isFarmerActivityReport(createForm.report_type)
        ? {
            analytics_type: createForm.analytics_type,
            ...(showDateFields
              ? {
                  start_date: createForm.start_date,
                  end_date: createForm.end_date,
                }
              : {}),
          }
        : {
            ...(showDateFields
              ? {
                  start_date: createForm.start_date,
                  end_date: createForm.end_date,
                }
              : {}),
            period: createForm.analytics_type,
            // order_type: createForm.order_type || null,
            category_id: null,
            generated_by_user_id: userId ? Number(userId) : 1,
            company_ids: (createForm.company_ids || [])
              .map((id) => Number(id))
              .filter((id) => !Number.isNaN(id)),
          };

      const payload = {
        report_name:
          REPORT_TYPES.find((type) => type.value === createForm.report_type)?.label ||
          "Report",
        report_type: createForm.report_type,
        generated_by: userId ? Number(userId) : 1,
        order_type: (() => {
          const orderTypes = normalizeOrderTypes(createForm.order_type);
          return orderTypes.length ? orderTypes.join(",") : null;
        })(),
        filters_json,
        ...(createForm.send_email && emailsToSend.length > 0
          ? isRevenueSummaryReport(createForm.report_type)
            ? {}
            : {
                send_email: true,
                emails: emailsToSend.join(","),
                download_format: isFarmerActivityReport(createForm.report_type)
                  ? "excel"
                  : createForm.download_format,
              }
          : {}),
      };
  
      const response = await apiService.post(API_ENDPOINTS.POST_reports_create, payload);
      if (response?.success === false) {
        throw new Error(response?.message || response?.error || "Failed to create report");
      }
  
      // Extract the newly created report from the response
      const createdReport =
        extractCreatedReport(response) || buildReportFromPayload(response, payload);
      const createdReportId =
        createdReport?.id ?? response?.data?.id ?? response?.id ?? null;

      if (
        isRevenueSummaryReport(createForm.report_type) &&
        createForm.send_email &&
        emailsToSend.length > 0 &&
        createdReportId
      ) {
        const revenueFormats = createForm.download_formats.length
          ? createForm.download_formats
          : ["excel"];
        const emailResponse = await apiService.post(API_ENDPOINTS.POST_reports_send_email, {
          report_id: createdReportId,
          emails: emailsToSend.join(","),
          download_format: revenueFormats.join(","),
          download_formats: revenueFormats,
        });
        if (emailResponse?.success === false) {
          throw new Error(
            emailResponse?.message || emailResponse?.error || "Report created but failed to send email"
          );
        }
      }
      
      // Optimistically add to the list WITHOUT refetching the entire list
      if (createdReport) {
        setReports((prev) => mergeReportIntoList(prev, createdReport));
      }
      loadReports();
      setCreateOpen(false);
      setCreateForm(defaultCreateForm());
      showSuccess(
        isRevenueSummaryReport(createForm.report_type) && createForm.send_email
          ? "Report created and email sent successfully"
          : response?.message || "Create Successfully"
      );
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || err?.detail || "Failed to create report";
      showError(message);
    } finally {
      setCreating(false);
    }
  };

  const handleViewDetail = async (reportId) => {
    const existing = reports.find((r) => String(r.id) === String(reportId));
    if (existing?.data_json && Object.keys(existing.data_json).length > 0) {
      setReportDetail(existing);
      setDetailOpen(true);
      return;
    }

    setDetailOpen(true);
    setDetailLoading(true);
    setReportDetail(null);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.GET_reports_detail}?id=${reportId}`
      );
      setReportDetail(extractReportDetail(response));
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || err?.detail || "Failed to load report details";
      showError(message);
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownloadReport = async (reportId, reportName, format = "pdf") => {
    if (!can("download")) {
      showError("You don't have permission to download reports");
      return;
    }
    if (!apiService.hasTokens()) {
      showError("Please login again to download the report.");
      return;
    }

    setPdfLoadingId(reportId);
    try {
      const endpoint = `${API_ENDPOINTS.POST_reports_generate_pdf}?id=${reportId}&export_type=${format}`;
      await apiService.downloadFromExportEndpoint(endpoint, {
        method: "post",
        data: null,
        fallbackFilename: reportName
          ? String(reportName).trim().replace(/[/\\?%*:|"<>]/g, "_")
          : `report_${reportId}`,
      });
      showSuccess("File downloaded successfully");
    } catch (err) {
      const message = await apiService.getExportErrorMessage(err);
      showError(message || "Failed to download file");
    } finally {
      setPdfLoadingId(null);
    }
  };

  const openDownloadMenu = (event, report) => {
    if (!can("download")) {
      showError("You don't have permission to download reports");
      return;
    }
    setDownloadMenuAnchor(event.currentTarget);
    setDownloadMenuReport(report);
  };

  const closeDownloadMenu = () => {
    setDownloadMenuAnchor(null);
    setDownloadMenuReport(null);
  };

  const handleRevenueDownloadSelect = (format) => {
    if (!downloadMenuReport) return;
    closeDownloadMenu();
    handleDownloadReport(downloadMenuReport.id, downloadMenuReport.report_name, format);
  };

  const openSendEmailDialog = (reportId) => {
    if (!can("send_mail")) {
      showError("You don't have permission to send report email");
      return;
    }
    setSendEmailReportId(reportId);
    setSendEmailInput("");
    setSendEmailList([]);
    setSendEmailError("");
    setSendEmailOpen(true);
  };

  const addSendEmailToList = () => {
    const result = tryAddReportEmail(sendEmailInput, sendEmailList);
    if (!result.ok) {
      setSendEmailError(result.error);
      return;
    }
    setSendEmailList((prev) => [...prev, result.email]);
    setSendEmailInput("");
    setSendEmailError("");
  };

  const removeSendEmailFromList = (email) => {
    setSendEmailList((prev) => prev.filter((item) => item !== email));
    setSendEmailError("");
  };

  const addCreateEmailToList = () => {
    const result = tryAddReportEmail(createForm.emailInput, createForm.emails);
    if (!result.ok) {
      setCreateForm((prev) => ({ ...prev, emailError: result.error }));
      return;
    }
    setCreateForm((prev) => ({
      ...prev,
      emails: [...prev.emails, result.email],
      emailInput: "",
      emailError: "",
    }));
  };

  const removeCreateEmailFromList = (email) => {
    setCreateForm((prev) => ({
      ...prev,
      emails: prev.emails.filter((item) => item !== email),
      emailError: "",
    }));
  };

  const handleSendEmailConfirm = async () => {
    if (sendEmailList.length === 0) {
      showError("Please add at least one email address");
      return;
    }
    setEmailLoadingId(sendEmailReportId);
    setSendEmailOpen(false);
    try {
      const response = await apiService.post(API_ENDPOINTS.POST_reports_send_email, {
        report_id: sendEmailReportId,
        emails: sendEmailList.join(","),
      });
      if (response?.success === false) {
        throw new Error(response?.message || response?.error || "Failed to send email");
      }
      showSuccess(response?.message || "Send Mail Successfully");
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || err?.detail || "Failed to send email";
      showError(message);
    } finally {
      setEmailLoadingId(null);
      setSendEmailReportId(null);
    }
  };

  const handleDeleteReport = async () => {
    if (!can("delete")) {
      showError("You don't have permission to delete reports");
      return;
    }
    const report = confirmDelete.report;
    if (!report?.id) return;
    setDeleteLoadingId(report.id);
    try {
      const response = await apiService.delete(
        `${API_ENDPOINTS.DELETE_daily_report}?id=${report.id}`
      );
      if (response?.success === false) {
        throw new Error(response?.message || response?.error || "Failed to delete report");
      }
      setConfirmDelete({ open: false, report: null });
      showSuccess(response?.message || "Delete Successfully");
      const wasLastItemOnPage = reports.length === 1;
      const nextPage =
        wasLastItemOnPage && currentPage > 1 ? currentPage - 1 : currentPage;
      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      } else {
        await loadReports({ silent: true });
      }
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : err?.message || err?.detail || "Failed to delete report";
      showError(message);
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const renderOrderAnalyticsTable = (orderAnalytics) => {
    if (!orderAnalytics) return null;
    const entries = Object.entries(orderAnalytics);
    return (
      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell align="right">Count</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([status, count]) => (
            <TableRow key={status}>
              <TableCell sx={{ textTransform: "capitalize" }}>{status}</TableCell>
              <TableCell align="right">{count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderDetailContent = (detail) => {
    if (!detail) return null;
    const overall = detail.data_json?.overall_company_data || {};
    const companiesData = detail.data_json?.companies || [];

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Report Name
            </Typography>
            <Typography fontWeight={600}>{detail.report_name}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Type
            </Typography>
            <Typography fontWeight={600}>
              {REPORT_TYPES.find((t) => t.value === detail.report_type)?.label ||
                detail.report_type}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Date Range
            </Typography>
            <Typography fontWeight={600}>
              {getDateRangeLabel(detail.filters_json)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Analytics Type
            </Typography>
            <Typography fontWeight={600}>
              {getAnalyticsTypeLabel(detail.analytics_type, detail.report_type)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Generated By
            </Typography>
            <Typography fontWeight={600}>{detail.generated_by_name}</Typography>
          </Grid>
        </Grid>

        <Typography variant="subtitle2" gutterBottom>
          Overall Order Status
        </Typography>
        {renderOrderAnalyticsTable(overall.order_analytics)}

        {companiesData.map((company) => (
          <Box key={company.company_id} sx={{ mt: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {company.company_name}
            </Typography>
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item xs={6} sm={3}>
                <Small color="text.secondary">Revenue</Small>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(company.revenue_analytics?.total_revenue)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Small color="text.secondary">Orders</Small>
                <Typography variant="body2" fontWeight={600}>
                  {company.revenue_analytics?.total_orders ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Small color="text.secondary">Farmers</Small>
                <Typography variant="body2" fontWeight={600}>
                  {company.farmers_analytics?.total_farmers ?? 0}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Small color="text.secondary">Active Farmers</Small>
                <Typography variant="body2" fontWeight={600}>
                  {company.farmers_analytics?.active_farmers ?? 0}
                </Typography>
              </Grid>
            </Grid>
            {renderOrderAnalyticsTable(company.order_analytics)}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box p={2}>
      <Card sx={{ p: 2 }}>
        <FlexBetween mb={2} flexWrap="wrap" gap={1.5} sx={{ justifyContent: "flex-end" }}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Rows</InputLabel>
              <Select value={pageSize} label="Rows" onChange={handlePageSizeChange}>
                {pageSizeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={() => loadReports()}
              disabled={loading}
            >
              Refresh
            </Button>
            <Tooltip title={can("create") ? "" : "No permission to create report"}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openCreateDialog}
                  disabled={!can("create")}
                  sx={{ opacity: !can("create") ? 0.6 : 1 }}
                >
                  Create Report
                </Button>
              </span>
            </Tooltip>
          </Box>
        </FlexBetween>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={reportTableContainerSx}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              ...reportTableSx,
              "& .MuiTableCell-head": { textAlign: "center" },
              "& .MuiTableCell-body": { textAlign: "center", verticalAlign: "middle" },
            }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell
                  className="header"
                  align="center"
                  sx={reportColumnSx.srNo}
                >
                  Sr No
                </StyledTableCell>
                <StyledTableCell
                  className="header"
                  align="center"
                  sx={reportColumnSx.reportName}
                >
                  Report Name
                </StyledTableCell>
                <StyledTableCell
                  className="header"
                  align="center"
                  sx={reportColumnSx.type}
                >
                  Type
                </StyledTableCell>
                <StyledTableCell
                  className="header"
                  align="center"
                  sx={{
                    ...reportColumnSx.dateRange,
                    display: { xs: "none", sm: "table-cell" },
                  }}
                >
                  Date Range
                </StyledTableCell>
                <StyledTableCell
                  className="header"
                  align="center"
                  sx={reportColumnSx.company}
                >
                  Company
                </StyledTableCell>
                <StyledTableCell
                  className="header"
                  align="center"
                  sx={{
                    ...reportColumnSx.generatedBy,
                    display: { xs: "none", lg: "table-cell" },
                  }}
                >
                  Generated By
                </StyledTableCell>
                <StyledTableCell
                  className="header"
                  align="center"
                  sx={reportColumnSx.generated}
                >
                  Generated
                </StyledTableCell>
                <StyledTableCell
                  className="header"
                  align="center"
                  sx={{ ...reportColumnSx.actions, ...actionsCellSx }}
                >
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <StyledTableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </StyledTableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Small color="text.secondary">
                      No reports found. Create one to get started.
                    </Small>
                  </StyledTableCell>
                </TableRow>
              ) : (
                reports.map((row, index) => (
                  <StyledTableRow key={row.id ?? `report-${index}`}>
                    <StyledTableCell
                      align="center"
                      sx={{ ...reportColumnSx.srNo, ...tableCellCenterSx }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500, textAlign: "center", width: "100%" }}
                      >
                        {(pagination.current_page - 1) * pagination.page_size + index + 1}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{ ...reportColumnSx.reportName, ...tableCellCenterSx }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{
                          textAlign: "center",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.report_name}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{ ...reportColumnSx.type, ...typeCellSx }}
                    >
                      <Box sx={typeContentSx}>
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: "center",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: 1.35,
                          }}
                        >
                          {REPORT_TYPES.find((t) => t.value === row.report_type)?.label ||
                            row.report_type}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{
                        ...reportColumnSx.dateRange,
                        ...tableCellCenterSx,
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          textAlign: "center",
                          width: "100%",
                          lineHeight: 1.35,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {getDateRangeLabel({
                          start_date: row.start_date,
                          end_date: row.end_date,
                        })}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{ ...reportColumnSx.company, ...typeCellSx }}
                    >
                      <ReportCompanyCell row={row} companies={companies} />
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{
                        ...reportColumnSx.generatedBy,
                        ...tableCellCenterSx,
                        display: { xs: "none", lg: "table-cell" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ textAlign: "center", width: "100%" }}
                      >
                        {row.generated_by_name}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{ ...reportColumnSx.generated, ...tableCellCenterSx }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", width: "100%" }}
                      >
                        {formatDate(row.generated_at)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{ ...reportColumnSx.actions, ...actionsCellSx }}
                    >
                      <Box sx={actionsContentSx}>
                      <Tooltip
                        title={
                          can("send_mail") ? "Send email" : "No permission to send email"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="default"
                            disabled={emailLoadingId === row.id || !can("send_mail")}
                            onClick={() => openSendEmailDialog(row.id)}
                            sx={{ opacity: !can("send_mail") ? 0.5 : 1 }}
                          >
                            {emailLoadingId === row.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Email fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          can("download") ? "Download" : "No permission to download"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="secondary"
                            disabled={pdfLoadingId === row.id || !can("download")}
                            onClick={(e) =>
                              isRevenueSummaryReport(row.report_type)
                                ? openDownloadMenu(e, row)
                                : handleDownloadReport(
                                    row.id,
                                    row.report_name,
                                    isFarmerActivityReport(row.report_type) ? "excel" : "pdf"
                                  )
                            }
                            sx={{ opacity: !can("download") ? 0.5 : 1 }}
                          >
                            {pdfLoadingId === row.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Download fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={can("delete") ? "Delete" : "No permission to delete"}
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleteLoadingId === row.id || !can("delete")}
                            onClick={() => {
                              if (!can("delete")) {
                                showError("You don't have permission to delete reports");
                                return;
                              }
                              setConfirmDelete({ open: true, report: row });
                            }}
                            sx={{ opacity: !can("delete") ? 0.5 : 1 }}
                          >
                            {deleteLoadingId === row.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Delete fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <AppPaginationBar
          pagination={pagination}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          itemLabel="reports"
        />
      </Card>

      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={closeDownloadMenu}
      >
        <MenuItem onClick={() => handleRevenueDownloadSelect("pdf")}>PDF</MenuItem>
        <MenuItem onClick={() => handleRevenueDownloadSelect("excel")}>Excel</MenuItem>
      </Menu>

      {/* Create Report Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Report</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Report Type</InputLabel>
                <Select
                  label="Report Type"
                  value={createForm.report_type}
                  onChange={(e) => {
                    const report_type = e.target.value;
                    setCreateForm((prev) => {
                      const analytics_type = getAnalyticsTypes(report_type).some(
                        (type) => type.value === prev.analytics_type
                      )
                        ? prev.analytics_type
                        : "weekly";

                      return {
                        ...prev,
                        report_type,
                        analytics_type,
                        ...(!shouldShowReportDateFields(report_type, analytics_type)
                          ? { start_date: "", end_date: "" }
                          : {}),
                        ...(isFarmerActivityReport(report_type)
                          ? { download_format: "excel", download_formats: ["excel"] }
                          : {}),
                      };
                    });
                  }}
                >
                  {REPORT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Analytics Type</InputLabel>
                <Select
                  label="Analytics Type"
                  value={createForm.analytics_type}
                  onChange={(e) => {
                    const analytics_type = e.target.value;
                    setCreateForm((prev) => ({
                      ...prev,
                      analytics_type,
                      ...((usesPeriodBasedAnalytics(prev.report_type) ||
                        isFarmerActivityReport(prev.report_type)) &&
                      analytics_type !== "custom"
                        ? { start_date: "", end_date: "" }
                        : {}),
                    }));
                  }}
                >
                  {getAnalyticsTypes(createForm.report_type).map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {shouldShowReportDateFields(createForm.report_type, createForm.analytics_type) && (
              <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={createForm.start_date}
                onChange={(e) => {
                  const start_date = e.target.value;
                  const today = getTodayDateString();
                  setCreateForm((prev) => {
                    let end_date = prev.end_date;
                    if (
                      end_date &&
                      (end_date < start_date || end_date > today)
                    ) {
                      end_date = start_date;
                    }
                    return { ...prev, start_date, end_date };
                  });
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: getTodayDateString() }}
                helperText="Any date up to today"
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={createForm.end_date}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, end_date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: createForm.start_date || undefined,
                  max: getTodayDateString(),
                }}
                helperText={
                  createForm.start_date
                    ? "From start date up to today"
                    : "Select start date first"
                }
                disabled={!createForm.start_date}
                size="small"
                required
              />
            </Grid>
              </>
            )}
            {!isRevenueSummaryReport(createForm.report_type) &&
              !isFarmerActivityReport(createForm.report_type) && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small" error={!!orderTypeError} required>
                <InputLabel required>Order Type</InputLabel>
                <Select
                  multiple
                  label="Order Type"
                  open={orderTypeMenuOpen}
                  onOpen={() => setOrderTypeMenuOpen(true)}
                  onClose={() => setOrderTypeMenuOpen(false)}
                  value={normalizeOrderTypes(createForm.order_type)}
                  onChange={(e) => {
                    const value = e.target.value;
                    const order_type =
                      typeof value === "string" ? value.split(",") : value;
                    setCreateForm((prev) => ({
                      ...prev,
                      order_type,
                    }));
                    if (order_type.length) {
                      setOrderTypeError("");
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 320 },
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected.length) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          Select order type
                        </Typography>
                      );
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const type = ORDER_TYPES.find((item) => item.value === value);
                          return (
                            <Chip
                              key={value}
                              label={type?.label || value}
                              size="small"
                              sx={{ maxWidth: 160 }}
                            />
                          );
                        })}
                      </Box>
                    );
                  }}
                >
                  {ORDER_TYPES.map((type) => {
                    const checked = normalizeOrderTypes(createForm.order_type).includes(
                      type.value
                    );
                    return (
                      <MenuItem key={type.value} value={type.value}>
                        <Checkbox checked={checked} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={type.label} />
                      </MenuItem>
                    );
                  })}
                  <ListSubheader
                    component="div"
                    disableSticky
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      zIndex: 1,
                      backgroundColor: "background.paper",
                      borderTop: "1px solid",
                      borderColor: "divider",
                      py: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOrderTypeMenuOpen(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </ListSubheader>
                </Select>
                <FormHelperText sx={{ minHeight: 20, m: 0 }}>
                  {orderTypeError || " "}
                </FormHelperText>
              </FormControl>
            </Grid>
            )}
            {!isRevenueSummaryReport(createForm.report_type) &&
              !isFarmerActivityReport(createForm.report_type) && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small" disabled={isCompanyUser || loadingCompanies}>
                <InputLabel id="report-companies-label">Companies</InputLabel>
                <Select
                  labelId="report-companies-label"
                  multiple
                  label="Companies"
                  open={companiesMenuOpen}
                  onOpen={() => setCompaniesMenuOpen(true)}
                  onClose={() => setCompaniesMenuOpen(false)}
                  value={createForm.company_ids}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreateForm((prev) => ({
                      ...prev,
                      company_ids:
                        typeof value === "string" ? value.split(",") : value,
                    }));
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 320 },
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected.length) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          All Companies
                        </Typography>
                      );
                    }
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => {
                          const company = companies.find(
                            (c) => String(c.id) === String(id)
                          );
                          return (
                            <Chip
                              key={id}
                              label={company?.name || id}
                              size="small"
                              sx={{ maxWidth: 160 }}
                            />
                          );
                        })}
                      </Box>
                    );
                  }}
                >
                  {companies.map((company) => {
                    const companyIdStr = String(company.id);
                    const checked = createForm.company_ids.includes(companyIdStr);
                    return (
                      <MenuItem key={company.id} value={companyIdStr}>
                        <Checkbox checked={checked} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={company.name} />
                      </MenuItem>
                    );
                  })}
                  {!loadingCompanies && companies.length > 0 && (
                    <ListSubheader
                      component="div"
                      disableSticky
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        zIndex: 1,
                        backgroundColor: "background.paper",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        py: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCompaniesMenuOpen(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </ListSubheader>
                  )}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Leave empty for all companies, or select one or more
                </Typography>
              </FormControl>
            </Grid>
            )}
            {isRevenueSummaryReport(createForm.report_type) && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Download Format</InputLabel>
                  <Select
                    multiple
                    label="Select Download Format"
                    value={createForm.download_formats}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCreateForm((prev) => ({
                        ...prev,
                        download_formats:
                          typeof value === "string" ? value.split(",") : value,
                      }));
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => {
                          const format = DOWNLOAD_FORMATS.find(
                            (item) => item.value === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={format?.label || value}
                              size="small"
                              sx={{ maxWidth: 160 }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {DOWNLOAD_FORMATS.map((format) => {
                      const checked = createForm.download_formats.includes(format.value);
                      return (
                        <MenuItem key={format.value} value={format.value}>
                          <Checkbox checked={checked} size="small" sx={{ py: 0 }} />
                          <ListItemText primary={format.label} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createForm.send_email}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          send_email: e.target.checked,
                          ...(e.target.checked
                            ? {}
                            : { download_format: "excel", download_formats: ["excel"] }),
                        }))
                      }
                    />
                  }
                  label="Send report on email"
                />
              </Box>
            </Grid>
            {createForm.send_email && (
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight={500} gutterBottom>
                  Email recipients
                </Typography>
                <ReportEmailChipInput
                  emails={createForm.emails}
                  inputValue={createForm.emailInput}
                  onInputChange={(value) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      emailInput: value,
                      emailError: "",
                    }))
                  }
                  onAdd={addCreateEmailToList}
                  onRemove={removeCreateEmailFromList}
                  error={createForm.emailError || ""}
                  placeholder="Add email"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Tooltip title={can("create") ? "" : "No permission to create report"}>
            <span>
              <Button
                variant="contained"
                onClick={handleCreateReport}
                disabled={creating || !can("create")}
                sx={{ opacity: !can("create") ? 0.6 : 1 }}
              >
                {creating ? <CircularProgress size={22} color="inherit" /> : "Create"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Report Details</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : reportDetail ? (
            renderDetailContent(reportDetail)
          ) : (
            <Typography color="text.secondary">No details available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog
        open={sendEmailOpen}
        onClose={() => !emailLoadingId && setSendEmailOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Send Report via Email</DialogTitle>
        <DialogContent>
          <ReportEmailChipInput
            emails={sendEmailList}
            inputValue={sendEmailInput}
            onInputChange={(value) => {
              setSendEmailInput(value);
              setSendEmailError("");
            }}
            onAdd={addSendEmailToList}
            onRemove={removeSendEmailFromList}
            error={sendEmailError}
            placeholder="Add email"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendEmailOpen(false)} disabled={!!emailLoadingId}>
            Cancel
          </Button>
          <Button
            onClick={handleSendEmailConfirm}
            variant="contained"
            disabled={sendEmailList.length === 0 || !!emailLoadingId}
          >
            {emailLoadingId ? <CircularProgress size={22} color="inherit" /> : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, report: null })}
        onConfirm={handleDeleteReport}
        title="Delete Report"
        message={`Are you sure you want to delete "${confirmDelete.report?.report_name || "this report"}"?`}
        confirmButtonText="Delete"
        confirmButtonColor="error"
        isLoading={!!deleteLoadingId}
      />
    </Box>
  );
};

export default Report;